import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { resetPassword } from '../services/authApiClient';
import PasswordInput from '../components/PasswordInput';
import FormButton from '../components/FormButton';
import ValidationMessage from '../components/ValidationMessage';
import FuturaSmileIcon from '../components/icons/FuturaSmileIcon';
import ChevronLeftIcon from '../components/icons/ChevronLeftIcon';
import toast from 'react-hot-toast';

const ResetPasswordPage: React.FC = () => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!token) {
            toast.error('Invalid or missing reset token.');
            return;
        }

        if (newPassword.length < 8) {
            toast.error('Password must be at least 8 characters long');
            return;
        }

        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        setIsSubmitting(true);
        try {
            await resetPassword(token, newPassword);
            setIsSuccess(true);
            toast.success('Password reset successfully!');
            setTimeout(() => navigate('/login'), 2000);
        } catch (err: any) {
            console.error('Reset password error:', err);
            toast.error(err.message || 'Failed to reset password');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="bg-gray-800 p-8 rounded-lg text-center max-w-md">
                    <h2 className="text-xl font-bold text-red-500 mb-4">Invalid Link</h2>
                    <p className="text-gray-300 mb-6">The password reset link is invalid or has expired.</p>
                    <Link
                        to="/forgot-password"
                        className="text-teal-400 hover:text-teal-300 underline"
                    >
                        Request a new one
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 flex">
            {/* Decorative Branding Panel */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                    style={{
                        backgroundImage: 'url(https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80)'
                    }}
                />
                <div className="absolute inset-0 bg-black/60" />
                <div className="relative z-10 flex flex-col justify-between p-8 h-full">
                    <div className="flex justify-between items-start">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 rounded-full bg-teal-600 shadow-lg">
                                <FuturaSmileIcon className="w-6 h-6 text-white" />
                            </div>
                            <h1 className="text-2xl font-bold text-white tracking-widest uppercase">
                                FuturaSmile
                            </h1>
                        </div>
                    </div>
                    <div className="max-w-md">
                        <blockquote className="text-2xl font-serif text-white leading-relaxed">
                            "Reset your password to regain access to your account."
                        </blockquote>
                    </div>
                </div>
            </div>

            {/* Form Panel */}
            <div className="flex-1 flex items-center justify-center p-4 lg:p-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="w-full max-w-md"
                >
                    <div className="mb-8">
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                        >
                            <h1 className="text-3xl font-bold text-gray-200 mb-2">
                                Set New Password
                            </h1>
                            <p className="text-gray-400">
                                Please enter your new password below.
                            </p>
                        </motion.div>
                    </div>

                    {!isSuccess ? (
                        <motion.form
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="space-y-6"
                            onSubmit={handleSubmit}
                        >
                            <PasswordInput
                                id="new-password"
                                name="new-password"
                                label="New Password"
                                value={newPassword}
                                onChange={setNewPassword}
                                placeholder="Enter new password"
                                required
                            />

                            <PasswordInput
                                id="confirm-password"
                                name="confirm-password"
                                label="Confirm Password"
                                value={confirmPassword}
                                onChange={setConfirmPassword}
                                placeholder="Confirm new password"
                                required
                            />

                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                            >
                                <FormButton
                                    type="submit"
                                    loading={isSubmitting}
                                    disabled={isSubmitting}
                                    fullWidth
                                    size="lg"
                                    variant="teal"
                                >
                                    {isSubmitting ? 'Resetting...' : 'Reset Password'}
                                </FormButton>
                            </motion.div>
                        </motion.form>
                    ) : (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-green-900/30 border border-green-800 rounded-lg p-6 text-center"
                        >
                            <div className="mb-4 flex justify-center">
                                <div className="bg-green-800/50 p-3 rounded-full">
                                    <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                            </div>
                            <h3 className="text-xl font-semibold text-green-300 mb-2">Success!</h3>
                            <p className="text-gray-300 mb-6">
                                Your password has been successfully reset. Redirecting to login...
                            </p>
                            <Link
                                to="/login"
                                className="bg-teal-600 hover:bg-teal-500 text-white font-bold py-2 px-6 rounded-full inline-block transition-colors"
                            >
                                Proceed to Login
                            </Link>
                        </motion.div>
                    )}

                    <div className="mt-8 text-center">
                        <Link
                            to="/login"
                            className="font-medium text-teal-400 hover:text-teal-300 transition-colors flex items-center justify-center gap-2"
                        >
                            <ChevronLeftIcon className="w-4 h-4" />
                            Back to Sign In
                        </Link>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
